import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Button,
  Container,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { tdsDeclarationCreate } from "../../../Services/Services";

export default function TdsDeclarationForm({ setDeclarations, setOpenAddModal }) {
  // खाली पंक्ति का टेम्पलेट
  const emptyRow = {
    truck_no: "",
    truck_owner: "",
    description: "",
    financial_year: "2025-26", // डिफ़ॉल्ट मान
  };

  const [tdsRows, setTdsRows] = useState([emptyRow]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  
  // वित्तीय वर्ष के विकल्प
  const financialYearOptions = [
    "2024-25",
    "2025-26",
    "2026-27",
    "2027-28",
    "2028-29",
  ];

  // फॉर्म में बदलाव संभालें
  const handleChange = (index, name, value) => {
    const updatedRows = [...tdsRows];
    updatedRows[index][name] = value;
    setTdsRows(updatedRows);
  };

  // नई पंक्ति जोड़ें
  const handleAddRow = () => {
    setTdsRows([...tdsRows, { ...emptyRow }]);
  };

  // पंक्ति हटाएं
  const handleRemoveRow = (index) => {
    const updated = [...tdsRows];
    updated.splice(index, 1);
    setTdsRows(updated);
  };

  // फॉर्म सबमिट करें
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // सभी पंक्तियों को सबमिट करें
      const response = await tdsDeclarationCreate(tdsRows);
      setDeclarations(response);
      setOpenAddModal(false);
      setSnackbar({
        open: true,
        message: "TDS Declarations created successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error creating TDS declarations:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to create TDS declarations.",
        severity: "error",
      });
    }
  };

  // सभी पंक्तियों की वैधता जांचें
  const validateForm = () => {
    return tdsRows.every(row => row.truck_no && row.truck_owner);
  };

  return (
    <Container maxWidth="xl">
      <form onSubmit={handleSubmit}>
        
        {tdsRows.map((row, index) => (
          <Box
            key={index}
            sx={{ 
              mb: 2, 
              p: 2, 
              border: '1px solid #e0e0e0', 
              borderRadius: 1,
              backgroundColor: 'background.paper'
            }}
          >
            {/* First Row - Basic Information */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              {/* Truck Number */}
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Truck Number *"
                  value={row.truck_no}
                  onChange={(e) =>
                    handleChange(index, "truck_no", e.target.value.toUpperCase())
                  }
                  fullWidth
                  required
                  placeholder="e.g., MH12AB1234"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>

              {/* Truck Owner */}
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Truck Owner *"
                  value={row.truck_owner}
                  onChange={(e) =>
                    handleChange(index, "truck_owner", e.target.value)
                  }
                  fullWidth
                  required
                  placeholder="Enter owner name"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* Financial Year */}
              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  select
                  label="Financial Year"
                  value={row.financial_year || "2025-26"}
                  onChange={(e) =>
                    handleChange(index, "financial_year", e.target.value)
                  }
                  fullWidth
                  SelectProps={{
                    native: true,
                  }}
                >
                  {financialYearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </TextField>
              </Grid>

              {/* Delete Button */}
              <Grid item xs={12} sm={1} md={1} sx={{ textAlign: 'center' }}>
                {tdsRows.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveRow(index)}
                    size="medium"
                    sx={{ mt: { xs: 0, sm: 1 } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>

            {/* Second Row - Description (Full Width) */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={row.description}
                  onChange={(e) =>
                    handleChange(index, "description", e.target.value)
                  }
                  fullWidth
                  multiline
                  rows={1}
                  placeholder="Additional details, remarks, or any other information..."
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        ))}

        {/* Buttons Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} mb={2}>
          <Box display="flex" alignItems="center">
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              sx={{ mr: 2 }}
            >
              Add Row
            </Button>
            <Typography variant="caption" color="textSecondary">
              {tdsRows.length} declaration(s) added
            </Typography>
          </Box>
          
          <Box display="flex" gap={2}>
            <Button 
              variant="outlined" 
              onClick={() => setOpenAddModal(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              type="submit"
              disabled={!validateForm()}
              size="large"
            >
              Submit Declarations
            </Button>
          </Box>
        </Box>
      </form>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}